import { get } from "lodash";
import { ColumnProps } from "primereact/column";
import { DataTableStateEvent } from "primereact/datatable";
import { ReactNode } from "react";
import { SORT } from "../../../constants";
import {
    ColumnOptions,
    DataTableSettings,
    UpdateDataTableSettingsFunction,
} from "../../../models/table.model";

interface SortableTablePropsConfig {
    isLoading: boolean;
    dataTableSettings: DataTableSettings;
    onDataTableSettingsChanged: UpdateDataTableSettingsFunction;
    columns?: ColumnOptions[];
}

interface SortableTableProps {
    sortField: string;
    sortMode?: "single" | "multiple" | undefined;
    sortIcon?: ReactNode;
    sortOrder: 1 | 0 | -1 | null | undefined;
    onSort?: (event: DataTableStateEvent) => void;
}

const useSortableProps = (props: SortableTablePropsConfig) => {
    const {
        isLoading,
        dataTableSettings,
        onDataTableSettingsChanged,
        columns = [],
    } = props;

    /* isLoading-prop is here because we maybe need to remove sortFunction from column-configs later */

    const columnWithSort = (column: ColumnProps) => {
        ({
            sortable: get(column, "sortable", true) ? true : false,
        }) as ColumnProps;
    };

    const onDataTableSort = (sortEvent: DataTableStateEvent) => {
        const { sortField, sortOrder } = sortEvent;

        let isDefaultSortField = false;

        // This prevents event from triggering if column has multiSortMenu
        if (columns.length > 0) {
            const column: ColumnOptions | undefined = columns.find(
                (col: ColumnOptions) =>
                    // For some reason we use columnName and sometimes sortField as an identifier which col is clicked
                    col.columnName === sortField ||
                    col.props.sortField === sortField
            );

            if (get(column, "props.isDefaultSortField", false)) {
                isDefaultSortField = true;
            }

            // Multimenu is opened
            if (
                column &&
                Array.isArray(column.multiSortMenu) &&
                column.multiSortMenu.length > 0
            ) {
                return;
            }
        }

        // const isSortAsc = sortOrder === SORT.ASC;

        onDataTableSettingsChanged((oldSettings: DataTableSettings) => {
            const newSortOrder =
                oldSettings.sortType === sortField // check if the same column is clicked again
                    ? !oldSettings.sortAsc // toggle between ascending and descending
                    : true; // first sort click should be ascending

            const doDeleteField =
                !isDefaultSortField && // the default sort field cannot be deleted
                oldSettings.sortType === sortField && // check if the same column is clicked again
                !oldSettings.sortAsc; // check if the column was already sorted descending, then we delete the sort

            return {
                ...oldSettings,
                sortAsc: doDeleteField ? null : newSortOrder,
                sortType: doDeleteField ? null : sortField,
            };
        });
    };

    const sortTableProps: SortableTableProps = {
        // props from https://primereact.org/datatable/ which are relatable to sort
        sortField: dataTableSettings.sortType || "",
        sortOrder: dataTableSettings.sortAsc === true ? SORT.ASC : SORT.DESC,
        sortMode: "single",
        /* sortIcon: () => {}, */
        onSort: onDataTableSort,
    };

    return { sortTableProps, columnWithSort };
};

export default useSortableProps;
